import { createEffect, createMemo, createSignal, type JSX, on, Show } from 'solid-js';
import { t } from '../i18n';
import { useBoxDetail, useReleases } from '../lib/box-lifecycle';
import { machineAccess, type PanelTarget, panelVerdict } from '../lib/fleet-machine-panel';
import { canRenameMachine, generatedName } from '../lib/fleet-rename';
import type { UtilBox } from '../lib/fleetUtilization';
import type { MachinePanel as Opener } from '../lib/machine-panel';
import type { MachineRemovals } from '../lib/machine-removals';
import { createMachineRenameWrite } from '../lib/name-write';
import { ActionDrawer } from './ActionDrawer';
import { MachineAbout } from './MachineAbout';
import { MachineActions } from './MachineActions';
import { MachineCleanup } from './MachineCleanup';
import { MachineDiagnosis } from './MachineDiagnosis';
import { MachinePanelAccess } from './MachinePanelAccess';
import { MachinePanelHead } from './MachinePanelHead';
import { MachinePanelRenamePencil } from './MachinePanelRenamePencil';
import { MachineRecentWork } from './MachineRecentWork';
import { MACHINE_RENAME_INPUT, MachineRename } from './MachineRename';
import { MachineResources } from './MachineResources';
import { Skeleton } from './Skeleton';

// The machine details panel (#6563, prototype `panel()`): a right-side sheet over the
// Fleet page, opened ONLY through the one opener (`createMachinePanel`, `lib/machine-panel.ts`)
// from a card, a List row, a Map square, a Needs attention row or the card menu, and from
// the url: `?machine=<id>` (owner ruling G10). It replaced the Manage dialog and the
// remove dialog (`BoxManageModal`, `RevokeRunnerModal`, both deleted).
//
// IT READS THE UTILIZATION ROW, THE ONE 20-SECOND POLL (owner ruling G3), resolved by id
// and rendered un-keyed: a poll re-colours the panel in place and never closes an open
// confirm or form. Only Instructions and Who it works for need `GET /v1/runners/:id`
// (the row carries neither `system_prompt` nor `owner_id`), and About reads its `arch`
// for "Up to date"; a machine from another org is never point-read (it 404s cross-org).
//
// FOCUS LANDS INSIDE on every open, a deep link included (#6563 v2, item 2): on the
// heading, or on the Remove confirm's first button when opened at Remove, through
// `ActionDrawer`'s `initialFocus` and the one dialog trap. The confirm renders off the
// row, never behind the point read, so it exists when the trap's microtask looks for it.
//
// RESOURCES AND RECENT WORK (#6565) read per machine, keyed by its id
// (`lib/machine-panel-reads.ts`), so switching machines never draws the last one's
// history or work under the new name; a machine from another org shows its readouts only.
//
// RENAME IS THE NAME'S OWN (#6566, prototype `panel()` head): a pencil beside the heading
// opens the inline form, and the card menu's "Rename" opens the panel in rename mode (the
// opener's `rename` target), the form up and its field focused. One form, no second one.
//
// REMOVE BELONGS TO THE PAGE (`createMachineRemovals`): the panel drives the current
// removal's confirm, and a removal whose token cascade failed is parked on the Fleet page
// with its retry (`MachineRemovalNotices`), so it stays visible after the panel moves on.
//
// THE JOBS-AT-ONCE ROW LIVES IN ABOUT (#6740): `MachineAbout` closes its section with it
// (`MachineJobsAtOnce`, variant="row"), for own-org and cross-org rows alike — the
// component renders the read-only text for a member, a viewer or a machine from another
// org. It is not in `MachineActions`, whose own header says the same.

export type MachinePanelProps = {
  readonly panel: Opener;
  /** The utilization read's machines, undefined until it first answers. */
  readonly boxes: readonly UtilBox[] | undefined;
  /** `canAdminWrite` on a settled role (owner ruling G21). */
  readonly canWrite: boolean;
  readonly token: string | null;
  /** The page's removals: the panel drives `current()`; failed cascades park on the page. */
  readonly removals: MachineRemovals;
  /** "View server" in the diagnosis: group the list by server and bring it into view. */
  readonly onViewServer: () => void;
  /** Re-read the roster behind the panel. */
  readonly onWritten: () => void;
  readonly notify: (message: string, tone?: 'success' | 'error') => void;
};

// Where an open's focus lands (#6563 v2, item 2, the header's focus rule): at Remove the
// inline confirm's first button, at rename the form's field, otherwise the drawer heading.
const panelInitialFocus = (
  body: HTMLDivElement | undefined,
  target: PanelTarget,
): 'title' | (() => HTMLElement | undefined) => {
  const removeFocus = (): HTMLElement | undefined =>
    body?.querySelector<HTMLElement>('[data-remove-confirm] button') ?? undefined;
  const renameFocus = (): HTMLElement | undefined =>
    body?.querySelector<HTMLElement>(`#${MACHINE_RENAME_INPUT}`) ?? undefined;
  if (target === 'remove') return removeFocus;
  return target === 'rename' ? renameFocus : 'title';
};

export function MachinePanel(props: MachinePanelProps): JSX.Element {
  const verdict = createMemo(() => panelVerdict(props.panel.id(), props.boxes));
  const box = createMemo((): UtilBox | null => {
    const v = verdict();
    return v.kind === 'open' ? v.box : null;
  });
  const access = createMemo(() => {
    const b = box();
    return b === null ? null : machineAccess(b, props.canWrite);
  });
  // Point-read only this org's own machine (a cross-org one 404s).
  const detailId = (): string | null => {
    const b = box();
    return b !== null && b.via_org === null ? b.id : null;
  };
  const detail = useBoxDetail(detailId, props.token);
  const releases = useReleases(() => (box() === null ? null : 'stable'), props.token);

  const remove = () => props.removals.current();

  // The rename form: open while renaming, closed by Save, Cancel or another open.
  const [renaming, setRenaming] = createSignal(false);
  const renameOffered = (): boolean => {
    const b = box();
    return b !== null && canRenameMachine(b, props.canWrite);
  };
  const rename = createMachineRenameWrite({
    box,
    token: props.token,
    notify: (m, tone) => props.notify(m, tone),
    onWritten: () => props.onWritten(),
    onDone: () => setRenaming(false),
  });

  // An open lands where its opener asked: at Remove, the inline confirm is up before the
  // trap looks for its first button. Opening another machine drops the last one's confirm.
  // Keyed on memos of the id and the access kind, never on the row: every re-read hands
  // over a new row object for the same machine, and re-running here on it would put back a
  // Remove confirm the person had just answered "Keep it" to.
  const boxId = createMemo(() => box()?.id);
  const accessKind = createMemo(() => access()?.kind);
  createEffect(
    on(
      [() => props.panel.id(), () => props.panel.target(), boxId, accessKind],
      ([id, target, boxId, kind]) => {
        rename.reset();
        setRenaming(target === 'rename' && kind === 'admin' && boxId === id);
        const r = remove();
        const held = r.state();
        if (held !== null && held.id !== id) r.reset();
        const b = box();
        if (target === 'remove' && kind === 'admin' && b !== null && boxId === id) {
          if (r.state()?.id !== b.id) r.ask(b);
        }
      },
    ),
  );

  let body: HTMLDivElement | undefined;
  const initialFocus = (): 'title' | (() => HTMLElement | undefined) =>
    panelInitialFocus(body, props.panel.target());

  const title = (): string => box()?.display_name ?? t('fleet.panel.missing_title');
  const close = (): void => {
    remove().reset();
    props.panel.close();
  };
  const toggleRename = (): void => {
    rename.reset();
    setRenaming((now) => !now);
  };

  return (
    <ActionDrawer
      open={verdict().kind !== 'closed'}
      title={title()}
      class="machine-panel"
      initialFocus={initialFocus()}
      titleAction={
        <MachinePanelRenamePencil
          offered={renameOffered()}
          expanded={renaming()}
          name={title()}
          onToggle={toggleRename}
        />
      }
      onClose={close}
    >
      <div class="machine-panel__body" ref={body}>
        <Show when={verdict().kind === 'loading'}>
          <div role="status">
            <span class="sr-only">{t('states.loading')}</span>
            <Skeleton height="12rem" />
          </div>
        </Show>
        <Show when={verdict().kind === 'missing'}>
          <p class="machine-panel__missing" role="status">
            {t('fleet.panel.missing')}
          </p>
        </Show>
        <Show when={box()}>
          {(b) => (
            <>
              <Show when={renaming() && renameOffered()}>
                <MachineRename
                  currentName={b().display_name}
                  generatedName={generatedName(b())}
                  pending={rename.pending()}
                  error={rename.error()}
                  onSave={rename.save}
                  onCancel={() => setRenaming(false)}
                />
              </Show>
              <MachinePanelHead box={b()} notify={props.notify} />
              <MachineDiagnosis
                box={b()}
                boxes={props.boxes ?? []}
                onViewServer={() => {
                  close();
                  props.onViewServer();
                }}
              />
              <MachineResources box={b()} token={props.token} />
              <Show when={b().via_org === null}>
                <MachineRecentWork box={b()} token={props.token} />
              </Show>
              <MachineAbout
                box={b()}
                arch={detail.data?.specs?.arch ?? null}
                releases={releases.data?.data ?? []}
                allBoxes={props.boxes ?? []}
                canWrite={props.canWrite}
                token={props.token}
                notify={props.notify}
                onWritten={props.onWritten}
              />
              <MachineCleanup box={b()} />
              <Show
                when={access()?.kind === 'admin'}
                fallback={<MachinePanelAccess box={b()} canWrite={props.canWrite} />}
              >
                <MachineActions
                  box={b()}
                  detail={detail}
                  token={props.token}
                  target={props.panel.target()}
                  remove={remove()}
                  onWritten={() => {
                    void detail.refetch();
                    props.onWritten();
                  }}
                />
              </Show>
            </>
          )}
        </Show>
      </div>
    </ActionDrawer>
  );
}
